import { useState, useMemo } from 'react';
import { Card, Badge, Button, Table, Form } from 'react-bootstrap';
import {
  RelatorioAtividadeDto,
  isReenvioDiretoCorregedoriaPendente,
} from '@/modules/reports/application/relatorios-atividades.service';
import Loading from '@/shared/components/Carregando';
import { getPrazoEnvioInfo } from '../utils/prazo-envio-utils';

interface RelatoriosAprovacaoListProps {
  relatorios: RelatorioAtividadeDto[];
  loading?: boolean;
  onView: (relatorio: RelatorioAtividadeDto) => void;
  onApproveBatch?: (relatorios: RelatorioAtividadeDto[]) => void;
  approvingBatch?: boolean;
}

export default function RelatoriosAprovacaoList({
  relatorios = [],
  loading = false,
  onView,
  onApproveBatch,
  approvingBatch = false,
}: RelatoriosAprovacaoListProps) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const aprovareis = useMemo(() => relatorios.filter((r) => r.status === 'enviado'), [relatorios]);
  const allAprovaveisSelected = aprovareis.length > 0 && aprovareis.every((r) => selectedIds.has(r.id));
  const someSelected = selectedIds.size > 0;

  const toggleOne = (id: number, canApprove: boolean) => {
    if (!canApprove) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllAprovaveis = () => {
    if (allAprovaveisSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(aprovareis.map((r) => r.id)));
    }
  };

  const handleAprovarSelecionados = () => {
    if (!onApproveBatch || selectedIds.size === 0) return;
    const selecionados = relatorios.filter((r) => selectedIds.has(r.id));
    onApproveBatch(selecionados);
    setSelectedIds(new Set());
  };
  const getStatusBadge = (relatorio: RelatorioAtividadeDto) => {
    switch (relatorio.status) {
      case 'enviado':
        return <Badge bg="warning">Aguardando decisão da Diretoria</Badge>;
      case 'aprovado':
        return relatorio.corregedoriaAprovadoEm ? (
          <Badge bg="success">Aprovado final (Corregedoria)</Badge>
        ) : (
          <Badge bg="info">Encaminhado para Corregedoria</Badge>
        );
      case 'reprovado':
        if (isReenvioDiretoCorregedoriaPendente(relatorio)) {
          return (
            <Badge bg="warning" text="dark">
              Corregedoria: aguardando reenvio direto pelo defensor
            </Badge>
          );
        }
        return relatorio.dataAprovacao ? (
          <Badge bg="danger">Reprovado pela Corregedoria</Badge>
        ) : (
          <Badge bg="danger">Reprovado pela Diretoria</Badge>
        );
      default:
        return <Badge bg="secondary">{relatorio.status}</Badge>;
    }
  };

  const getPrazoForaBadge = (relatorio: RelatorioAtividadeDto) => {
    const info = getPrazoEnvioInfo(relatorio);
    if (!info?.isForaPrazo) return null;

    const tooltip = `Prazo final: ${info.prazoFinalLabel}${info.enviadoEmLabel ? ` | Enviado em: ${info.enviadoEmLabel}` : ''}`;
    return (
      <Badge bg="warning" text="dark" title={tooltip}>
        Fora do prazo
      </Badge>
    );
  };

  const getTipoLabel = (tipo: string) => {
    return tipo === 'civil' ? 'Cível' : 'Criminal';
  };

  const formatarData = (data: string | Date | undefined) => {
    if (!data) return '-';
    
    try {
      const date = data instanceof Date ? data : new Date(data);
      if (isNaN(date.getTime())) return '-';
      
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (error) {
      return '-';
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (relatorios.length === 0) {
    return (
      <Card>
        <Card.Body className="text-center text-muted">
          <p className="mb-0">Nenhum relatório encontrado para aprovação.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm">
      <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h5 className="mb-0">Relatórios para Aprovação ({relatorios.length})</h5>
        {onApproveBatch && aprovareis.length > 0 && (
          <Button
            variant="primary"
            size="sm"
            disabled={!someSelected || approvingBatch}
            onClick={handleAprovarSelecionados}
          >
            {approvingBatch ? 'Aprovando...' : `Aprovar selecionados (${selectedIds.size})`}
          </Button>
        )}
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Table striped hover className="mb-0">
            <thead className="bg-light">
              <tr>
                <th style={{ width: '40px' }}>
                  {aprovareis.length > 0 && (
                    <Form.Check
                      type="checkbox"
                      checked={allAprovaveisSelected}
                      onChange={toggleAllAprovaveis}
                      aria-label="Selecionar todos aguardando aprovação"
                    />
                  )}
                </th>
                <th>Defensor</th>
                <th>Defensoria/Vara</th>
                <th>Período</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Data de envio (defensor)</th>
                <th>Data Aprovação</th>
                <th>Data Reprovação</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {relatorios.map((relatorio) => (
                <tr key={relatorio.id}>
                  <td>
                    {relatorio.status === 'enviado' ? (
                      <Form.Check
                        type="checkbox"
                        checked={selectedIds.has(relatorio.id)}
                        onChange={() => toggleOne(relatorio.id, true)}
                        aria-label={`Selecionar relatório ${relatorio.periodo}`}
                      />
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>{relatorio.defensorNome || 'Não informado'}</td>
                  <td>{relatorio.defensoriaName}</td>
                  <td>{relatorio.periodo}</td>
                  <td>{getTipoLabel(relatorio.tipo)}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      {getStatusBadge(relatorio)}
                      {getPrazoForaBadge(relatorio)}
                    </div>
                  </td>
                  <td>{formatarData(relatorio.dataEnvio)}</td>
                  <td>{formatarData(relatorio.dataAprovacao)}</td>
                  <td>
                    {relatorio.status === 'reprovado' && (
                      <span title={relatorio.justificativaReprovacao || ''}>
                        {formatarData(relatorio.dataReprovacao)}
                        {relatorio.reprovadoPorNome && (
                          <small className="d-block text-muted">{relatorio.reprovadoPorNome}</small>
                        )}
                      </span>
                    )}
                    {relatorio.status !== 'reprovado' && '-'}
                  </td>
                  <td>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => onView(relatorio)}
                      >
                        Visualizar
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
}
